import { useEffect, useRef, useState } from 'react';
import type { KeyboardEvent, PointerEvent, ReactNode, RefObject } from 'react';
import { useIntl } from 'react-intl';
import * as stylex from '@stylexjs/stylex';
import '@astryxdesign/core/theme/tokens.stylex';
import { colorVars, durationVars, radiusVars, shadowVars, spacingVars } from '@astryxdesign/core/theme/tokens.stylex';
import { Button } from '@astryxdesign/core/Button';
import { HStack } from '@astryxdesign/core/HStack';
import { IconButton } from '@astryxdesign/core/IconButton';
import { Icon } from '@astryxdesign/core/Icon';
import { Text } from '@astryxdesign/core/Text';
import { Badge } from '@astryxdesign/core/Badge';
import { TileFillContext, useInView } from '@qronstat/ui';
import { Eye, EyeOff, GripVertical, MoveDiagonal2 } from 'lucide-react';
import { layoutColumns } from '@qronstat/shared';
import { resizeTile } from './layout';
import type { LayoutMode, Placed } from './layout';
import { WidgetRenderer } from './WidgetRenderer';
import { KpiTileUnit } from '../widgets/KpisWidget';
import { WidgetFallback } from '../widgets/skeletons';
const edgeZone = 72;
const scrollStep = 14;
const keyHeightStep = 40;
function scrollParent(el: HTMLElement | null): HTMLElement | null {
  let node = el?.parentElement ?? null;
  while (node) {
    const overflow = getComputedStyle(node).overflowY;
    if ((overflow === 'auto' || overflow === 'scroll') && node.scrollHeight > node.clientHeight) return node;
    node = node.parentElement;
  }
  return document.scrollingElement as HTMLElement | null;
}
function edgeStep(scroller: HTMLElement, y: number): number {
  const box = scroller === document.scrollingElement ? {
    top: 0,
    bottom: window.innerHeight
  } : scroller.getBoundingClientRect();
  return y < box.top + edgeZone ? -scrollStep : y > box.bottom - edgeZone ? scrollStep : 0;
}
export function CustomizeBar({
  isPhone,
  canReset,
  saving,
  announcement,
  onReset,
  onCancel,
  onSave
}: {
  isPhone: boolean;
  canReset: boolean;
  saving: boolean;
  announcement: string;
  onReset: () => void;
  onCancel: () => void;
  onSave: () => void;
}) {
  const intl = useIntl();
  return <div role="region" aria-label={intl.formatMessage({
    id: 'layout.title'
  })} className="x7wzq59 x13vifvy x1u8a7rm xqfq6s9 xj6sv8s xce4md1 xrrkdod x1hviunn x1prclbq xmkeg23 x1y0btm7 xad5do x14hfi27 x78zum5 x1a02dak x6s0dn4 x1qughib x1txdalj">
      <div className="x78zum5 xdt5ytf xeuugli x861rx3 x1iyjqo2">
        <Text weight="semibold">{intl.formatMessage({
          id: 'layout.title'
        })}</Text>
        <Text size="sm" color="secondary">
          {intl.formatMessage({
          id: isPhone ? 'layout.hintPhone' : 'layout.hint'
        })}
        </Text>
      </div>
      <HStack gap={1} vAlign="center" wrap="wrap">
        <Button label={intl.formatMessage({
        id: 'layout.reset'
      })} variant="ghost" size="sm" isDisabled={!canReset} onClick={onReset} />
        <Button label={intl.formatMessage({
        id: 'common.cancel'
      })} variant="ghost" size="sm" onClick={onCancel} />
        <Button label={intl.formatMessage({
        id: 'common.save'
      })} variant="primary" size="sm" isLoading={saving} onClick={onSave} />
      </HStack>
      <span role="status" aria-live="polite" className="x10l6tqk x1i1rx1s xjm9jq1 xb3r6kr x1hyvwdk xuxw1ft">
        {announcement}
      </span>
    </div>;
}
function usePointerSession() {
  const cleanup = useRef<(() => void) | null>(null);
  useEffect(() => () => cleanup.current?.(), []);
  return (pointerId: number, onMove: (e: globalThis.PointerEvent) => void, onTick: (() => void) | null, onEnd: () => void) => {
    cleanup.current?.();
    let frame = 0;
    const move = (e: globalThis.PointerEvent) => {
      if (e.pointerId !== pointerId) return;
      e.preventDefault();
      onMove(e);
    };
    const end = (e: globalThis.PointerEvent) => {
      if (e.pointerId === pointerId) cleanup.current?.();
    };
    const tick = () => {
      onTick?.();
      frame = requestAnimationFrame(tick);
    };
    window.addEventListener('pointermove', move, {
      passive: false
    });
    window.addEventListener('pointerup', end);
    window.addEventListener('pointercancel', end);
    if (onTick) frame = requestAnimationFrame(tick);
    cleanup.current = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', end);
      window.removeEventListener('pointercancel', end);
      if (frame) cancelAnimationFrame(frame);
      cleanup.current = null;
      onEnd();
    };
  };
}
export function useTileDrag(gridRef: RefObject<HTMLDivElement | null>, order: readonly string[], onMove: (id: string, to: number) => void) {
  const [dragging, setDragging] = useState<string | null>(null);
  const orderRef = useRef(order);
  const moveRef = useRef(onMove);
  const lastTarget = useRef(-1);
  useEffect(() => {
    orderRef.current = order;
    moveRef.current = onMove;
  });
  const session = usePointerSession();
  const retarget = (id: string, point: {
    x: number;
    y: number;
  }) => {
    const grid = gridRef.current;
    if (!grid) return;
    const box = grid.getBoundingClientRect();
    const local = {
      x: point.x - box.left,
      y: point.y - box.top
    };
    const nodes = Array.from(grid.querySelectorAll<HTMLElement>(':scope > [data-widget-id]'));
    const target = nodes.findIndex(node => local.x >= node.offsetLeft && local.x <= node.offsetLeft + node.offsetWidth && local.y >= node.offsetTop && local.y <= node.offsetTop + node.offsetHeight);
    const from = orderRef.current.indexOf(id);
    if (target < 0 || from < 0 || target === from || target === lastTarget.current) return;
    lastTarget.current = target;
    moveRef.current(id, target);
  };
  const start = (id: string, event: PointerEvent<HTMLButtonElement>) => {
    if (event.button !== 0) return;
    event.preventDefault();
    let point = {
      x: event.clientX,
      y: event.clientY
    };
    const scroller = scrollParent(gridRef.current);
    lastTarget.current = -1;
    setDragging(id);
    session(event.pointerId, e => {
      point = {
        x: e.clientX,
        y: e.clientY
      };
      lastTarget.current = -1;
      retarget(id, point);
    }, () => {
      if (!scroller) return;
      const step = edgeStep(scroller, point.y);
      if (step) {
        scroller.scrollBy(0, step);
        retarget(id, point);
      }
    }, () => setDragging(null));
  };
  return {
    dragging,
    start
  };
}
export function tileHeight(grid: HTMLElement | null, id: string): number {
  const node = grid?.querySelector<HTMLElement>(`[data-widget-id="${CSS.escape(id)}"] > [data-tile-body]`);
  return node?.offsetHeight ?? 0;
}
export function useTileResize(gridRef: RefObject<HTMLDivElement | null>, mode: LayoutMode, onResize: (id: string, size: {
  w: number;
  h: number;
}) => void) {
  const [resizing, setResizing] = useState<string | null>(null);
  const resizeRef = useRef(onResize);
  useEffect(() => {
    resizeRef.current = onResize;
  });
  const session = usePointerSession();
  const start = (placed: Placed, event: PointerEvent<HTMLButtonElement>) => {
    if (event.button !== 0) return;
    event.preventDefault();
    const grid = gridRef.current;
    if (!grid) return;
    const style = getComputedStyle(grid);
    const inner = grid.clientWidth - parseFloat(style.paddingLeft) - parseFloat(style.paddingRight);
    const columnWidth = inner / layoutColumns;
    const origin = {
      x: event.clientX,
      y: event.clientY
    };
    const startHeight = placed.h ?? tileHeight(grid, placed.unit.id);
    const section = grid.querySelector<HTMLElement>(`[data-widget-id="${CSS.escape(placed.unit.id)}"]`);
    const startWidth = section && columnWidth > 0 ? Math.round(section.offsetWidth / columnWidth) : placed.w;
    setResizing(placed.unit.id);
    session(event.pointerId, e => resizeRef.current(placed.unit.id, resizeTile({
      startWidth,
      startHeight,
      dx: e.clientX - origin.x,
      dy: e.clientY - origin.y,
      columnWidth,
      kind: placed.unit.kind,
      mode
    })), null, () => setResizing(null));
  };
  return {
    resizing,
    start
  };
}
export function GridTile({
  placed,
  width,
  eager,
  label,
  editing,
  active,
  children
}: {
  placed: Placed;
  width: number;
  eager: boolean;
  label: string;
  editing: boolean;
  active: boolean;
  children?: ReactNode;
}) {
  const [viewRef, visible] = useInView<HTMLElement>(!eager && !editing);
  const fixed = placed.h !== null && !placed.hidden;
  const content = placed.unit.item ? <KpiTileUnit def={placed.unit.def as never} item={placed.unit.item} /> : <WidgetRenderer def={placed.unit.def} />;
  return <section ref={viewRef} data-widget-id={placed.unit.id} aria-label={label} {...{
    0: {
      className: "xeuugli xdqyycr x9f619"
    },
    1: {
      className: "xeuugli xdqyycr x9f619 x1y8v6su"
    }
  }[!!fixed << 0]} style={{
    gridColumn: `span ${width}`
  }}>
      <div data-tile-body="" {...{
      0: {
        className: "x1n2onr6 xeuugli x5yr21d"
      },
      4: {
        className: "x1n2onr6 xeuugli x5yr21d xb3r6kr"
      },
      2: {
        className: "x1n2onr6 xeuugli x5yr21d x1hviunn x1eybxrj x107h2d7 xj8wus6 x1hl8ikr xe97l14 xuedmi6"
      },
      6: {
        className: "x1n2onr6 xeuugli x5yr21d xb3r6kr x1hviunn x1eybxrj x107h2d7 xj8wus6 x1hl8ikr xe97l14 xuedmi6"
      },
      1: {
        className: "x1n2onr6 xeuugli x5yr21d xaatb59 x12mipk2 xoegz02"
      },
      5: {
        className: "x1n2onr6 xeuugli x5yr21d xb3r6kr xaatb59 x12mipk2 xoegz02"
      },
      3: {
        className: "x1n2onr6 xeuugli x5yr21d x1hviunn x1eybxrj xj8wus6 x1hl8ikr xe97l14 xuedmi6 xaatb59 x12mipk2 xoegz02"
      },
      7: {
        className: "x1n2onr6 xeuugli x5yr21d xb3r6kr x1hviunn x1eybxrj xj8wus6 x1hl8ikr xe97l14 xuedmi6 xaatb59 x12mipk2 xoegz02"
      }
    }[!!fixed << 2 | !!editing << 1 | !!active << 0]} style={fixed ? {
      height: placed.h ?? undefined
    } : undefined}>
        {placed.hidden ? null : <TileFillContext.Provider value={fixed}>
            {visible ? editing ? <div inert className="x5yr21d x47corl x87ps6o">
                  {content}
                </div> : content : <WidgetFallback />}
          </TileFillContext.Provider>}
        {children}
      </div>
    </section>;
}
export function TileControls({
  placed,
  label,
  index,
  count,
  mode,
  measure,
  onDragStart,
  onResizeStart,
  onMove,
  onResize,
  onToggleHidden
}: {
  placed: Placed;
  label: string;
  index: number;
  count: number;
  mode: LayoutMode;
  measure: () => number;
  onDragStart: (event: PointerEvent<HTMLButtonElement>) => void;
  onResizeStart: (event: PointerEvent<HTMLButtonElement>) => void;
  onMove: (to: number) => void;
  onResize: (size: {
    w: number;
    h: number;
  }) => void;
  onToggleHidden: () => void;
}) {
  const intl = useIntl();
  const moveKeys = (event: KeyboardEvent<HTMLButtonElement>) => {
    const back = event.key === 'ArrowUp' || event.key === 'ArrowLeft';
    const forward = event.key === 'ArrowDown' || event.key === 'ArrowRight';
    if (event.key === 'Home') onMove(0);else if (event.key === 'End') onMove(count - 1);else if (back && index > 0) onMove(index - 1);else if (forward && index < count - 1) onMove(index + 1);else return;
    event.preventDefault();
  };
  const sizeKeys = (event: KeyboardEvent<HTMLButtonElement>) => {
    const step = mode === 'phone' ? 30 : 5;
    const delta = event.key === 'ArrowRight' ? {
      dx: step,
      dy: 0
    } : event.key === 'ArrowLeft' ? {
      dx: -step,
      dy: 0
    } : event.key === 'ArrowDown' ? {
      dx: 0,
      dy: keyHeightStep
    } : event.key === 'ArrowUp' ? {
      dx: 0,
      dy: -keyHeightStep
    } : null;
    if (!delta) return;
    event.preventDefault();
    onResize(resizeTile({
      startWidth: placed.w,
      startHeight: placed.h ?? measure(),
      dx: delta.dx,
      dy: delta.dy,
      columnWidth: 1,
      kind: placed.unit.kind,
      mode
    }));
  };
  const moveLabel = intl.formatMessage({
    id: 'layout.move'
  }, {
    title: label
  });
  const resizeLabel = intl.formatMessage({
    id: 'layout.resize'
  }, {
    title: label
  });
  return <>
      {placed.hidden ? <div className="x78zum5 x6s0dn4 x1txdalj xbktkl8 x126nfab x3kqhmp x1hviunn x1de1mus x1ks1olk">
          <Text weight="medium" maxLines={1}>
            {label}
          </Text>
          <Badge label={intl.formatMessage({
        id: 'layout.hidden'
      })} variant="neutral" />
        </div> : null}
      <div className="x10l6tqk x1atx4j1 x1f889gz xzkaem6 x78zum5 x6s0dn4 x195vfkc x1i3ajwb xh6dtrn x1prclbq x1i5ehqx">
        <button type="button" aria-label={moveLabel} aria-keyshortcuts="ArrowUp ArrowDown Home End" title={moveLabel} onKeyDown={moveKeys} onPointerDown={onDragStart} className="x3nfvp2 x6s0dn4 xl56j7k x1td3qas x10w6t97 x2lah0s xc342km xh6dtrn xjbqb8w xtao9ic xqwr325 x1jm3nie xch2mbi x5ve5x3 x87ps6o">
          <Icon icon={GripVertical} />
        </button>
        <IconButton label={intl.formatMessage({
        id: placed.hidden ? 'layout.show' : 'layout.hide'
      })} icon={<Icon icon={placed.hidden ? Eye : EyeOff} />} variant="ghost" size="sm" onClick={onToggleHidden} />
      </div>
      {placed.hidden ? null : <button type="button" aria-label={resizeLabel} aria-keyshortcuts="ArrowLeft ArrowRight ArrowUp ArrowDown" title={resizeLabel} onKeyDown={sizeKeys} onPointerDown={onResizeStart} className="x10l6tqk xtijo5x x1ey2m1c xzkaem6 x1td3qas x10w6t97 x3nfvp2 xuk3077 x13a6bvl xfawy5m xc342km xh6dtrn xjbqb8w xqwr325 x16djdpl x5ve5x3 x87ps6o">
          <Icon icon={MoveDiagonal2} size="sm" />
        </button>}
    </>;
}